import {
  clearSession,
  createIdempotencyKey,
  getContextOptions,
  login,
  request,
  selectContext,
  setAccessToken,
  setStoredContext,
  setStoredPermissions
} from "./client";
import type {
  CompletePublicTenantProvisioningRequest,
  ConfirmFirstLoginCurrencyRequest,
  CreateFirstLoginProductRequest,
  FirstLoginProductResponse,
  FirstLoginState,
  ProvisionedWorkspaceSession,
  PublicRegistrationAccepted,
  PublicRegistrationStatusResponse,
  StartPublicRegistrationRequest,
  VerifyPublicRegistrationResponse,
  TenantProvisioningResult
} from "./public-signup-types";

const CONTEXT_ATTEMPTS = 8;
const CONTEXT_RETRY_DELAY_MS = 350;

export function startPublicRegistration(
  payload: StartPublicRegistrationRequest,
  idempotencyKey = createIdempotencyKey()
): Promise<PublicRegistrationAccepted> {
  return request<PublicRegistrationAccepted>(
    "/api/v1/public/registrations",
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(payload)
    },
    null
  );
}

export function verifyPublicRegistration(
  registrationId: string,
  code: string,
  idempotencyKey = createIdempotencyKey()
): Promise<VerifyPublicRegistrationResponse> {
  return request<VerifyPublicRegistrationResponse>(
    `/api/v1/public/registrations/${encodeURIComponent(registrationId)}/verify`,
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify({ code })
    },
    null
  );
}

export function resendPublicRegistration(
  registrationId: string,
  idempotencyKey = createIdempotencyKey()
): Promise<PublicRegistrationAccepted> {
  return request<PublicRegistrationAccepted>(
    `/api/v1/public/registrations/${encodeURIComponent(registrationId)}/resend`,
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey }
    },
    null
  );
}

export function getPublicRegistrationStatus(
  registrationId: string,
  accessGrant: string
): Promise<PublicRegistrationStatusResponse> {
  return request<PublicRegistrationStatusResponse>(
    `/api/v1/public/registrations/${encodeURIComponent(registrationId)}/status`,
    { headers: { "X-Registration-Access": accessGrant } },
    null
  );
}

export function completePublicTenantProvisioning(
  registrationId: string,
  accessGrant: string,
  payload: CompletePublicTenantProvisioningRequest,
  idempotencyKey = createIdempotencyKey()
): Promise<TenantProvisioningResult> {
  return request<TenantProvisioningResult>(
    `/api/v1/public/registrations/${encodeURIComponent(registrationId)}/complete`,
    {
      method: "POST",
      headers: {
        "Idempotency-Key": idempotencyKey,
        "X-Registration-Access": accessGrant
      },
      body: JSON.stringify(payload)
    },
    null
  );
}

export function getFirstLoginState(): Promise<FirstLoginState> {
  return request<FirstLoginState>("/api/v1/onboarding/first-login");
}

export function confirmFirstLoginCurrency(
  payload: ConfirmFirstLoginCurrencyRequest,
  idempotencyKey = createIdempotencyKey()
): Promise<FirstLoginState> {
  return request<FirstLoginState>(
    "/api/v1/onboarding/first-login/currency",
    {
      method: "PUT",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(payload)
    }
  );
}

export function createFirstLoginProduct(
  payload: CreateFirstLoginProductRequest,
  idempotencyKey = createIdempotencyKey()
): Promise<FirstLoginProductResponse> {
  return request<FirstLoginProductResponse>(
    "/api/v1/onboarding/first-login/products",
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(payload)
    }
  );
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function sameContext(
  left: { tenantId: string; branchId: string },
  right: { tenantId: string; branchId: string }
): boolean {
  return left.tenantId.toLowerCase() === right.tenantId.toLowerCase()
    && left.branchId.toLowerCase() === right.branchId.toLowerCase();
}

export async function authenticateProvisionedWorkspace(
  email: string,
  password: string
): Promise<ProvisionedWorkspaceSession> {
  // Public signup is a security-boundary handoff. Never let a stale token,
  // tenant context, permission snapshot or manager gate survive into the newly
  // provisioned owner's authentication attempt.
  clearSession();

  const authentication = await login(email, password);
  if (authentication.requiresMfa) {
    throw new Error("NEW_OWNER_MFA_UNEXPECTED");
  }
  if (!authentication.accessToken) {
    throw new Error("NEW_OWNER_AUTH_TOKEN_MISSING");
  }
  if (authentication.message === "PASSWORD_CHANGE_REQUIRED") {
    throw new Error("NEW_OWNER_PASSWORD_CHANGE_UNEXPECTED");
  }

  // Keep the identity token in memory only until a single unambiguous tenant
  // context has been proven and selected. Persisting it earlier could leave the
  // browser with a new token plus an old stored workspace after a failed handoff.
  let contexts = await getContextOptions(authentication.accessToken);
  for (let attempt = 1; contexts.length === 0 && attempt < CONTEXT_ATTEMPTS; attempt++) {
    await delay(CONTEXT_RETRY_DELAY_MS);
    contexts = await getContextOptions(authentication.accessToken);
  }

  if (contexts.length === 0) {
    throw new Error("NEW_OWNER_CONTEXT_NOT_READY");
  }

  if (contexts.length !== 1) {
    throw new Error("NEW_OWNER_CONTEXT_AMBIGUOUS");
  }

  const context = contexts[0];
  const selection = await selectContext(
    authentication.accessToken,
    context.tenantId,
    context.branchId
  );

  if (!sameContext(selection.context, context)) {
    throw new Error("NEW_OWNER_CONTEXT_SELECTION_MISMATCH");
  }

  setAccessToken(selection.accessToken);
  setStoredContext(selection.context);
  setStoredPermissions(selection.permissions);
  return { contextSelected: true };
}
